import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ATHLETE_LANDING, BASE_URL } from "../constants";
import { getPlatformKey } from "../utils/utils";
import IndexShell from "./ForBusiness/Dashboard/IndexShell";
import { landingPathFor } from "./auth/v2/landingPath";
import { Btn, Card, EmptyState, Loading, PageHeader } from "./ForBrands/ui/BrandUI";
import Icon from "./ForBrands/ui/icons";
import { fmtMoney } from "./ForAthletes/v2/athleteUtils";
import { VALUE_CARD_WORDS, performanceRange, valueBasisOf } from "./ForAthletes/PlayerProfile/profileModel";
import {
  Bar,
  DEFAULT_AVATAR,
  MetaChips,
  ProfileCardShell,
  RECRUITING_KEYS,
  RangeMeter,
  RingScore,
  SectionTitle,
  SocialLinks,
  faceCrop,
  linkKeyOf,
} from "./ForAthletes/v2/profileSections";
import MetricInfo from "./ForAthletes/v2/MetricInfo";
import { METRIC_INFO } from "./ForAthletes/v2/metricDescriptions";

// Public, no-login athlete card reached via the SHARE button (/p/:athleteId),
// in the V2 design: the Player Index frame (IndexShell) around the same
// hero and section blocks as the athlete's own V2 profile, read-only. It
// shows the privacy-safe fields the public endpoint returns and nothing
// else; the endpoint leaves out what the athlete's privacy toggles hide.
// Each metric card carries its short description and the (i) that opens
// the long one (MetricInfo), as on the profile.

// Roles that have a full in-app athlete profile view. A signed-in user with
// any of these gets bounced from the public card to the real profile.
const BUSINESS_PERMS = [
  "IS_ADMIN", "IS_CACHE_AI", "IS_COACH", "IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD",
];

// Where the wordmark goes for a visitor: the public Player Index.
const PUBLIC_INDEX = "/p/business/dashboard";

// Platforms SocialLinks draws with their own icon (one button per platform,
// at most five), then the profile sites (RECRUITING_KEYS: 247Sports, On3,
// ESPN), each its own link as on the athlete's profile (a saved link belongs
// to the one its host names). Every other link on the profile, a second link
// on the same platform included, still shows, with the generic link glyph,
// so the card links every URL it did before.
const ICON_PLATFORMS = ["instagram", "x", "twitter", "tiktok", "youtube", "facebook", "snapchat", "hudl"];
const MAX_ICON_LINKS = 5;

const splitSocials = (links) => {
  const seen = new Set();
  const icon = [];
  let socials = 0;
  links.forEach((url) => {
    const key = getPlatformKey(url);
    if (ICON_PLATFORMS.includes(key)) {
      const platform = key === "twitter" ? "x" : key;
      if (seen.has(platform) || socials >= MAX_ICON_LINKS) return;
      seen.add(platform);
      socials += 1;
      icon.push(url);
      return;
    }
    const site = linkKeyOf(url);
    if (!RECRUITING_KEYS.has(site) || seen.has(site)) return;
    seen.add(site);
    icon.push(url);
  });
  return { icon, other: links.filter((url) => !icon.includes(url)) };
};

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return "";
  }
};

const OtherLinks = ({ links }) =>
  links.map((url) => (
    <a
      key={url}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={hostOf(url) || "Link"}
      title={hostOf(url) || "Link"}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-bm-border text-bm-ink hover:bg-bm-subtle"
    >
      <Icon name="link" size={17} />
    </a>
  ));

const midOf = (range) => {
  if (!range) return null;
  const from = typeof range.from === "number" ? range.from : null;
  const to = typeof range.to === "number" ? range.to : null;
  const min = typeof range.min === "number" ? range.min : from ?? 0;
  const max = typeof range.max === "number" ? range.max : to ?? min;
  const value = from != null && to != null ? Math.round((from + to) / 2) : Math.round((min + max) / 2);
  return { floor: min, ceiling: max, value };
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : null);

const PublicAthleteProfile = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // ?preview=1: the athlete's own "Public profile" menu item, which shows
  // them the card visitors see instead of sending them to Home.
  const preview = new URLSearchParams(location.search).get("preview") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Auth-aware redirect: the public card is for logged-OUT visitors. A
  // signed-in business user (coach/admin/recruiter/etc.) should land on the
  // full in-app profile instead.
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const reduxUser = useSelector((s) => s.user?.user);
  const permissions = reduxUser?.permissions || [];
  const isBusinessUser = permissions.some((p) => BUSINESS_PERMS.includes(p));
  const isOwnProfile = permissions.includes("IS_ATHLETE") && reduxUser?.athleteId === athleteId;
  // An athlete viewing their OWN public link should land on their full
  // dashboard, not the stripped public card, unless they asked to preview it.
  const isOwnAthlete = !preview && isOwnProfile;
  const willRedirect = !!token && (isBusinessUser || isOwnAthlete);
  // Logged out, or the signed-in user's profile has resolved.
  const authResolved = !token || !!reduxUser?.email;
  const [authTimedOut, setAuthTimedOut] = useState(false);

  useEffect(() => {
    // Bound the wait for a stale/invalid token so we don't spin forever.
    const t = setTimeout(() => setAuthTimedOut(true), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!token) return;
    if (isBusinessUser) {
      navigate(`/business/athlette/profile/${athleteId}`, { replace: true });
    } else if (isOwnAthlete) {
      navigate(ATHLETE_LANDING, { replace: true });
    }
  }, [token, isBusinessUser, isOwnAthlete, athleteId, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch(`${BASE_URL}/api/users/public/athlete/${athleteId}/`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [athleteId]);

  // A signed-in viewer who stays here (an athlete previewing their card, a
  // supporter, rep or intern) gets one way back to their own home; a
  // visitor, or a stale token that never resolved, gets the two ways in.
  const myHome = token && reduxUser?.email ? landingPathFor(permissions) : null;
  const homeTo = myHome || PUBLIC_INDEX;
  // The wordmark's name says where it goes: the viewer's own home, or the
  // public Player Index.
  const homeLabel = myHome ? "My home" : "Player Index";
  const topbar = myHome ? (
    <Btn variant="secondary" to={myHome}>Go to my home</Btn>
  ) : (
    <>
      <Btn variant="secondary" to="/login">Log in</Btn>
      <Btn to="/register">Join CACHE</Btn>
    </>
  );

  // Hold while a signed-in user's role is still resolving (or the redirect
  // for a business user is in flight) so we never flash the public card to
  // someone who's about to be sent to the full profile. The topbar stays
  // empty meanwhile for the same reason.
  if (loading || (token && !authResolved && !authTimedOut) || willRedirect) {
    return (
      <IndexShell homeTo={homeTo} homeLabel={homeLabel} right={null}>
        <Loading label="Loading profile..." />
      </IndexShell>
    );
  }

  if (error || !data) {
    return (
      <IndexShell homeTo={homeTo} homeLabel={homeLabel} right={topbar}>
        <PageHeader eyebrow="Athlete profile" title="Profile not available" />
        <EmptyState
          action={
            myHome ? (
              <Btn to={myHome}>Go to my home</Btn>
            ) : (
              <Btn onClick={() => navigate("/register")}>Join CACHE</Btn>
            )
          }
        >
          This athlete profile could not be found or is no longer shared.
        </EmptyState>
      </IndexShell>
    );
  }

  const fullName = `${data.name || ""} ${data.surname || ""}`.trim() || "Athlete";
  const contrib = data.contributions || {};
  // Driven by the athlete's Step-6 privacy controls: the endpoint only
  // returns cacheScore / socials when the matching toggle is on.
  const hasScore = data.cacheScore !== undefined && data.cacheScore !== null;
  const socials = Array.isArray(data.socials) ? data.socials : [];
  const { icon: iconLinks, other: otherLinks } = splitSocials(socials);
  const confidence = capitalize(data.cacheScoreConfidence);
  // Each school and team under what it is, from the same identity the
  // profile reads (the endpoint sends them by slot): College (a pro's
  // University), High school (past high school, the last one), Club, Team
  // for a pro's team, and the college level, an estimate marked as one.
  const collegeLevel = data.collegeLevel?.code
    ? data.collegeLevel.estimated ? `${data.collegeLevel.label || data.collegeLevel.code} (estimated)` : data.collegeLevel.code
    : "";
  const chips = [
    [data.sport, data.level].filter(Boolean).join(" · "),
    data.jerseyNumber ? `#${data.jerseyNumber}` : null,
    data.position ? `Pos: ${data.position}` : null,
    data.college ? `College: ${data.college}` : null,
    data.university ? `University: ${data.university}` : null,
    data.highSchool ? `High school: ${data.highSchool}` : null,
    data.club ? `Club: ${data.club}` : null,
    data.proTeam ? `Team: ${data.proTeam}` : null,
    collegeLevel ? `College level: ${collegeLevel}` : null,
    data.location ? `Hometown: ${data.location}` : null,
  ].filter(Boolean);

  // The card's one value module (D3), as the endpoint names it
  // (valueBasis): CacheValue for an onboarded athlete, PerformanceValue
  // (its floor and ceiling around its point value) in the same place for
  // one who has not onboarded; a card without the field is CacheValue. Each
  // shows only when the endpoint sends it (the athlete's value toggle).
  const basis = valueBasisOf(data.valueBasis);
  const words = VALUE_CARD_WORDS[basis];
  const valueRange = basis === "performanceValue" ? performanceRange(data.performanceValue) : midOf(data.cacheValue);
  const cacheNilx = midOf(data.cacheShare);
  const valueCards = [valueRange, cacheNilx].filter(Boolean).length;
  const nr = data.nilRates || {};
  const nilTiles = [
    { label: "Events", value: nr.events },
    { label: "Social Media Post", value: nr.socialPost },
    { label: "Reel / TikTok", value: nr.reel ?? nr.reelStory },
    { label: "Story", value: nr.story ?? nr.reelStory },
    { label: "Long form video", value: nr.longVideo },
    { label: "Repost", value: nr.share },
  ].filter((t) => typeof t.value === "number");

  // The athlete looking at their own card: say it is the visitor view.
  const ownPreview = !!token && preview && isOwnProfile;
  // The sign-up pitch is for visitors (and the athlete's preview of what
  // visitors see), not for someone already signed in.
  const showPitch = !myHome || ownPreview;

  return (
    <IndexShell homeTo={homeTo} homeLabel={homeLabel} right={topbar}>
      <PageHeader
        eyebrow={ownPreview ? "Public profile preview" : "Athlete profile"}
        title={fullName}
        subtitle={ownPreview ? "This is what visitors see when you share your profile." : null}
        actions={
          socials.length > 0 ? (
            <span className="flex flex-wrap items-center gap-2">
              <SocialLinks links={iconLinks} />
              <OtherLinks links={otherLinks} />
            </span>
          ) : null
        }
      />

      <div className="flex flex-col gap-4">
        <MetaChips chips={chips} />

        {/* Photo, then CacheScore and Contributions when the athlete shares them */}
        <div className={hasScore ? "grid gap-4 md:grid-cols-[minmax(200px,260px)_1fr_1fr]" : "flex"}>
          <div
            className={`overflow-hidden rounded-[12px] border border-bm-border bg-bm-mutedbg ${
              hasScore ? "" : "aspect-[4/5] w-full max-w-[260px]"
            }`}
          >
            <img
              src={faceCrop(data.avatar || DEFAULT_AVATAR)}
              alt={fullName}
              className="h-full min-h-[240px] w-full object-cover object-top"
            />
          </div>
          {hasScore && (
            <>
              <ProfileCardShell>
                <SectionTitle title={METRIC_INFO.cacheScore.title} subtitle={METRIC_INFO.cacheScore.short} right={<MetricInfo metric="cacheScore" />} />
                <div className="flex flex-1 items-center justify-center">
                  <RingScore value={data.cacheScore} confidence={confidence} />
                </div>
              </ProfileCardShell>
              {/* Each pillar out of 10; one the endpoint does not send shows the en dash. */}
              <ProfileCardShell>
                <SectionTitle
                  title={METRIC_INFO.contributions.title}
                  subtitle={METRIC_INFO.contributions.short}
                  right={<MetricInfo metric="contributions" />}
                />
                <div className="flex flex-1 flex-col justify-center gap-5">
                  <Bar label="Performance" value={contrib.performance} />
                  <Bar label="Visibility" value={contrib.visibility} />
                  <Bar label="Off-court impact" value={contrib.offCourt} />
                </div>
              </ProfileCardShell>
            </>
          )}
        </div>

        {/* The value card (CacheValue or PerformanceValue) and CacheNILx, each only when the athlete shares it */}
        {valueCards > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            {valueRange && (
              <ProfileCardShell>
                <SectionTitle title={words.title} subtitle={words.subtitle} right={<MetricInfo metric={basis} />} />
                <RangeMeter range={valueRange} />
              </ProfileCardShell>
            )}
            {cacheNilx && (
              <ProfileCardShell>
                <SectionTitle title={METRIC_INFO.cacheNilx.title} subtitle={METRIC_INFO.cacheNilx.short} right={<MetricInfo metric="cacheNilx" />} />
                <RangeMeter range={cacheNilx} badge={capitalize(data.cacheShare?.tier)} badgeTone="passive" />
              </ProfileCardShell>
            )}
          </div>
        )}

        {/* NILx rate card */}
        {nilTiles.length > 0 && (
          <ProfileCardShell>
            <SectionTitle title={METRIC_INFO.nilxRates.title} subtitle={METRIC_INFO.nilxRates.short} right={<MetricInfo metric="nilxRates" />} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nilTiles.map((t) => (
                <div key={t.label} className="flex flex-col gap-1 rounded-[8px] border border-bm-border bg-white px-4 py-[14px]">
                  <span className="bm-body-s text-bm-muted">{t.label}</span>
                  <span className="bm-num-m text-bm-ink">{fmtMoney(t.value)}</span>
                </div>
              ))}
            </div>
          </ProfileCardShell>
        )}

        {showPitch && (
          <Card padding="p-8 sm:p-10" className="flex flex-col items-center gap-2 text-center">
            <h2 className="bm-h-m text-bm-ink">Want the full picture?</h2>
            <p className="max-w-md bm-body-m text-bm-muted">
              See {data.name || "this athlete"}&apos;s complete profile, stats, valuation, and engagement on CACHE.
            </p>
            <Btn className="mt-4" onClick={() => navigate("/register")}>
              See full information
            </Btn>
          </Card>
        )}

        <p className="pt-4 text-center bm-body-s text-bm-muted">Powered by CACHE</p>
      </div>
    </IndexShell>
  );
};

export default PublicAthleteProfile;
